import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, Briefcase, Globe, Mail, Check } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/layout/Breadcrumbs';

const FALCO_PHOTO = "https://customer-assets.emergentagent.com/job_54396c88-8cf9-4315-926b-d20a7ab432d7/artifacts/uqz5vdch_2026-03-21_13-47-36.png";

export default function OverMij() {
  const { language } = useLanguage();
  const basePath = language === 'en' ? '/en' : '';

  const experience = [
    {
      company: 'Yellowgrape',
      role: 'SEO Specialist',
      years: '2024 – heden',
      description: language === 'nl'
        ? 'Beheer van een divers portfolio van high-growth e-commerce klanten. Focus op het vertalen van business goals naar schaalbare SEO-strategieën.'
        : 'Managing a diverse portfolio of high-growth e-commerce clients. Focus on translating business goals into scalable SEO strategies.'
    },
    {
      company: 'Led Wereld',
      role: 'SEO Specialist',
      years: '2023 – 2024',
      description: language === 'nl'
        ? 'Internationale groei over de Nederlandse, Belgische en Duitse markten. Uitgebreide SEO-strategieën afgestemd op lokale zoeklandschappen.'
        : 'International growth across Dutch, Belgian and German markets. Comprehensive SEO strategies tailored to local search landscapes.'
    },
    {
      company: 'Artefact',
      role: 'SEO Specialist',
      years: '2022 – 2023',
      description: language === 'nl'
        ? 'Strategische partner voor wereldwijde merken, waaronder TASSIMO en L\'OR Espresso (Jacobs Douwe Egberts) en CitizenM.'
        : 'Strategic partner for global brands, including TASSIMO and L\'OR Espresso (Jacobs Douwe Egberts), and CitizenM.'
    },
    {
      company: language === 'nl' ? 'Alpina (voorheen Diks)' : 'Alpina (formerly Diks)',
      role: 'SEO Specialist',
      years: '2019 – 2022',
      description: language === 'nl'
        ? 'Organische traffic getransformeerd van 30.000 naar 100.000+ maandelijkse bezoekers in 3.5 jaar. De site-architectuur geherstructureerd wat heeft geleid tot significante groei, gecombineerd met autoriteitsopbouw en het dichten van content gaps. Verzekeringen is een zeer competitieve markt waarin domeinautoriteit opbouwen essentieel is.'
        : 'Transformed organic traffic from 30,000 to 100,000+ monthly visitors in 3.5 years. Restructured the site architecture which led to significant growth, combined with authority building and closing content gaps. Insurance is a highly competitive market where building domain authority is essential.'
    },
    {
      company: 'Trivago',
      role: 'SEO Off-Page Specialist',
      years: '2014 – 2016',
      description: language === 'nl'
        ? 'De fundering van mijn carrière. Beheer van kwalitatieve backlink portfolio\'s en data-driven PR campagnes voor de Nederlandse en Belgische markten.'
        : 'The foundation of my career. Managing quality backlink portfolios and data-driven PR campaigns for the Dutch and Belgian markets.'
    }
  ];

  const strengths = [
    {
      title: language === 'nl' ? 'Geen bureau-overhead' : 'No agency overhead',
      description: language === 'nl'
        ? 'Ik werk volledig remote zonder dure kantoorruimte. Die besparing vertaalt zich naar betere tarieven voor jou.'
        : 'I work fully remote without expensive office space. Those savings translate into better rates for you.'
    },
    {
      title: language === 'nl' ? 'Directe communicatie' : 'Direct communication',
      description: language === 'nl'
        ? 'Geen account managers of tussenlagen. Je werkt direct met de specialist die de strategie maakt én uitvoert.'
        : 'No account managers or intermediaries. You work directly with the specialist who creates and executes the strategy.'
    },
    {
      title: language === 'nl' ? '10+ jaar ervaring' : '10+ years experience',
      description: language === 'nl'
        ? 'Van in-house bij Trivago tot bureau en freelance — ik heb gewerkt met merken van startup tot enterprise en weet wat werkt.'
        : 'From in-house at Trivago to agency and freelance — I\'ve worked with brands from startup to enterprise and know what works.'
    }
  ];

  return (
    <div data-testid="over-mij-page">
      <SEOHead
        title={language === 'nl' ? 'Over Falco Keiman | Freelance SEO Specialist sinds 2014' : 'About Falco Keiman | Dutch SEO specialist with international experience'}
        description={language === 'nl'
          ? 'Freelance SEO specialist Falco Keiman. Ervaring bij Trivago, Alpina (voorheen Diks), Artefact en Yellowgrape. Holistische SEO-aanpak vanuit Veenendaal.'
          : 'Native Dutch SEO specialist with 10+ years experience. Previously at Trivago, Alpina, Artefact and Yellowgrape. Deep expertise in travel, ecommerce and financial services.'}
        canonical="https://www.kesult.com/over-mij"
        lang={language}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Person",
          "name": "Falco Keiman",
          "jobTitle": "Freelance SEO Specialist",
          "url": "https://www.kesult.com/over-mij",
          "worksFor": { "@type": "Organization", "name": "Kesult" },
          "sameAs": ["https://www.linkedin.com/in/falco-keiman/"]
        }}
      />
      {/* Hero - White with photo */}
      <section className="relative min-h-[85vh] flex items-center bg-white overflow-hidden" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="max-w-xl pt-8 lg:pt-0">
              <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-5">
                {language === 'nl' ? 'Over mij' : 'About me'}
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold leading-tight text-[#08080A] mb-6" data-testid="about-title">
                Falco Keiman
              </h1>
              <p className="text-lg text-[#555] leading-relaxed mb-8">
                {language === 'nl'
                  ? <>Hey! Ik ben <strong className="text-[#08080A]">Falco</strong>. Ik bied freelance SEO consultancy voor ambitieuze ondernemers en groeiende MKB-bedrijven in Nederland. Van startups tot scale-ups — ik heb een no-nonsense aanpak en lever kwaliteit die resultaat oplevert.</>
                  : <>Hey! I'm <strong className="text-[#08080A]">Falco</strong>. I provide freelance SEO consultancy for ambitious entrepreneurs and growing SMBs in the Netherlands. From startups to scale-ups — I have a no-nonsense approach and deliver quality that gets results.</>
                }
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to={`${basePath}/contact`}>
                  <button className="bg-[#4286F4] text-white font-semibold px-8 py-3.5 rounded-full hover:bg-[#3070d4] transition-colors inline-flex items-center gap-2" data-testid="about-cta-primary">
                    {language === 'nl' ? 'Neem contact op' : 'Get in touch'}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </Link>
                <a href="https://www.linkedin.com/in/falco-keiman/" target="_blank" rel="noopener noreferrer">
                  <button className="border-2 border-[#E5E5E5] text-[#333] font-semibold px-8 py-3.5 rounded-full hover:border-[#4286F4] hover:text-[#4286F4] transition-colors inline-flex items-center gap-2" data-testid="about-cta-linkedin">
                    LinkedIn
                  </button>
                </a>
              </div>
            </div>

            <div className="hidden lg:flex justify-end">
              <img
                src={FALCO_PHOTO}
                alt="Falco Keiman - Freelance SEO Specialist"
                className="w-full max-w-[440px] aspect-[3/4] object-cover object-top rounded-3xl"
                data-testid="about-photo"
              />
            </div>
          </div>
        </div>
      </section>

      <Breadcrumbs />

      {/* Background Story */}
      <section className="py-20 lg:py-28 bg-[#FAFAFA]" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
              {language === 'nl' ? 'Mijn achtergrond' : 'My background'}
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-6">
              {language === 'nl' ? 'Van in-house en bureau naar freelance SEO specialist' : 'From in-house and agency to freelance SEO specialist'}
            </h2>
            <div className="space-y-4 text-[#555] leading-relaxed">
              <p>
                {language === 'nl'
                  ? 'Mijn carrière in digital marketing begon tijdens mijn International Business studie. Op zoek naar een rol die aansloot bij mijn academische achtergrond, solliciteerde ik voor een Off-page SEO positie bij Trivago — ondanks dat ik op dat moment geen ervaring had. Die sprong in het diepe markeerde het begin van mijn SEO carrière.'
                  : 'My career in digital marketing began during my International Business studies. Seeking a role that aligned with my academic background, I applied for an Off-page SEO position at Trivago — despite having no prior experience at the time. That leap of faith marked the beginning of my SEO career.'}
              </p>
              <p>
                {language === 'nl'
                  ? 'Hoewel ik startte met een focus op het bouwen van autoriteit, groeide mijn interesse snel. Ik raakte gefascineerd door hoe de verschillende pijlers van zoekmachine optimalisatie met elkaar verbonden zijn. Deze drive leidde me om Technical SEO en Content Strategy te beheersen, waardoor ik evolueerde van een enthousiast naar een holistische SEO specialist die begrijpt dat echte groei ontstaat op het snijvlak van tech, content en authority.'
                  : 'While I started with a focus on building authority, my curiosity quickly grew. I became fascinated by how the different pillars of search engine optimization interconnect. This drive led me to master Technical SEO and Content Strategy, evolving from an enthusiast into a holistic SEO specialist who understands that real growth happens at the intersection of tech, content, and authority.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Timeline */}
      <section className="py-20 lg:py-28 bg-white" data-testid="experience-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
            {language === 'nl' ? 'Werkervaring' : 'Experience'}
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-14">
            {language === 'nl' ? '10+ jaar in SEO' : '10+ years in SEO'}
          </h2>

          <div className="space-y-6">
            {experience.map((exp, index) => (
              <div
                key={index}
                className="group grid grid-cols-1 lg:grid-cols-[120px_1fr] gap-6 lg:gap-10 p-8 lg:p-10 rounded-2xl border border-gray-100 hover:border-[#4286F4]/30 hover:shadow-lg transition-all duration-300"
                data-testid={`experience-${index}`}
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#4286F4]/10 rounded-xl flex items-center justify-center text-[#4286F4]">
                    <Briefcase className="h-5 w-5" />
                  </div>
                </div>
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-2">
                    <h3 className="text-xl font-bold text-[#08080A]">{exp.company}</h3>
                    <span className="text-sm text-[#999] font-mono">{exp.years}</span>
                  </div>
                  <p className="text-[#4286F4] font-semibold text-sm mb-3">{exp.role}</p>
                  <p className="text-[#555] leading-relaxed max-w-3xl">{exp.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Kesult */}
      <section className="py-20 lg:py-28 bg-[#08080A] text-white" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
              {language === 'nl' ? 'Waarom Kesult' : 'Why Kesult'}
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold mb-6">
              {language === 'nl'
                ? 'Geen bureau, geen overhead, geen gedoe'
                : 'No agency, no overhead, no hassle'}
            </h2>
            <p className="text-[#A1A1AA] leading-relaxed">
              {language === 'nl'
                ? 'Ik begrijp dat ROI voor elk bedrijf essentieel is. Mijn aanpak stelt mij in staat om de overhead laag te houden, waardoor ik SEO-diensten kan aanbieden tegen een fractie van de kosten van een groter bureau.'
                : 'I understand that ROI is everything for any business. My approach allows me to keep overheads low, offering SEO services at a fraction of the cost of a larger agency.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {strengths.map((item, i) => (
              <div key={i} className="p-8 rounded-2xl border border-white/10 hover:border-[#4286F4]/50 transition-colors duration-300">
                <h3 className="text-lg font-bold mb-3">{item.title}</h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study: Alpina (Diks.nl) */}
      <section className="py-20 lg:py-28 bg-white" data-testid="case-study-alpina">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
            {language === 'nl' ? 'Case study' : 'Case study'}
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#08080A] mb-4">
            {language === 'nl' ? 'Alpina (voorheen Diks.nl)' : 'Alpina (formerly Diks.nl)'}
          </h2>
          <p className="text-[#555] text-lg leading-relaxed max-w-3xl mb-12">
            {language === 'nl'
              ? 'Van mei 2019 tot december 2022 was ik als SEO Specialist verantwoordelijk voor de organische groei van Diks.nl en de bijbehorende labels. Door de site-architectuur te herstructureren, autoriteit op te bouwen en content gaps te dichten, groeide het organisch verkeer van 30.000 naar meer dan 100.000 maandelijkse bezoekers. In november 2022 is Diks.nl gemigreerd naar Alpina.nl als onderdeel van een volledige rebranding — deze migratie viel ook onder mijn verantwoordelijkheid. Verzekeringen is een van de meest competitieve markten in Nederland — resultaten behalen vereist een combinatie van technische kennis, strategisch denken en volharding.'
              : 'From May 2019 to December 2022, I was responsible for the organic growth of Diks.nl and its associated labels as SEO Specialist. By restructuring the site architecture, building authority and closing content gaps, organic traffic grew from 30,000 to over 100,000 monthly visitors. In November 2022, Diks.nl migrated to Alpina.nl as part of a full rebrand — this migration was also my responsibility. Insurance is one of the most competitive markets in the Netherlands — achieving results requires a combination of technical knowledge, strategic thinking and persistence.'}
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            <div className="rounded-2xl border border-gray-100 overflow-hidden bg-[#FAFAFA]">
              <div className="p-5 border-b border-gray-100">
                <h3 className="font-bold text-[#08080A] text-lg">Diks.nl</h3>
                <p className="text-[#555] text-sm mt-1">
                  {language === 'nl'
                    ? 'Organisch verkeer en geindexeerde pagina\'s — groei van ~30K naar 100K+ maandelijkse bezoekers'
                    : 'Organic traffic and indexed pages — growth from ~30K to 100K+ monthly visitors'}
                </p>
              </div>
              <img
                src="https://customer-assets.emergentagent.com/job_0357c4fc-e52a-47ac-a21c-eb4f9800644c/artifacts/2a5o7cfo_diks.nl.png"
                alt={language === 'nl' ? 'Diks.nl organisch verkeer groei grafiek via Ahrefs' : 'Diks.nl organic traffic growth chart via Ahrefs'}
                className="w-full"
                loading="lazy"
              />
            </div>

            <div className="rounded-2xl border border-gray-100 overflow-hidden bg-[#FAFAFA]">
              <div className="p-5 border-b border-gray-100">
                <h3 className="font-bold text-[#08080A] text-lg">Brommerverzekering.nl</h3>
                <p className="text-[#555] text-sm mt-1">
                  {language === 'nl'
                    ? 'Sublabel van Diks.nl — organisch verkeer groei door gerichte content en autoriteitsopbouw'
                    : 'Sub-label of Diks.nl — organic traffic growth through targeted content and authority building'}
                </p>
              </div>
              <img
                src="https://customer-assets.emergentagent.com/job_0357c4fc-e52a-47ac-a21c-eb4f9800644c/artifacts/a1pa2pkl_brommerverzekering.nl.png"
                alt={language === 'nl' ? 'Brommerverzekering.nl organisch verkeer groei grafiek via Ahrefs' : 'Brommerverzekering.nl organic traffic growth chart via Ahrefs'}
                className="w-full"
                loading="lazy"
              />
            </div>

            <div className="rounded-2xl border border-gray-100 overflow-hidden bg-[#FAFAFA]">
              <div className="p-5 border-b border-gray-100">
                <h3 className="font-bold text-[#08080A] text-lg">Geennee.nl</h3>
                <p className="text-[#555] text-sm mt-1">
                  {language === 'nl'
                    ? 'Sublabel van Diks.nl — organisch verkeer groei door SEO-strategie en contentoptimalisatie'
                    : 'Sub-label of Diks.nl — organic traffic growth through SEO strategy and content optimisation'}
                </p>
              </div>
              <img
                src="https://customer-assets.emergentagent.com/job_0357c4fc-e52a-47ac-a21c-eb4f9800644c/artifacts/limapgva_geennee.nl.png"
                alt={language === 'nl' ? 'Geennee.nl organisch verkeer groei grafiek via Ahrefs' : 'Geennee.nl organic traffic growth chart via Ahrefs'}
                className="w-full"
                loading="lazy"
              />
            </div>
          </div>

          <p className="text-[#555] text-sm italic">
            {language === 'nl'
              ? 'Bron: Ahrefs. De gearceerde periode toont de periode mei 2019 – december 2022 waarin ik actief was als SEO Specialist.'
              : 'Source: Ahrefs. The highlighted period shows May 2019 – December 2022 during which I was active as SEO Specialist.'}
          </p>
        </div>
      </section>

      {/* Linkbuilding Results */}
      <section className="py-20 lg:py-28 bg-[#FAFAFA]" data-testid="linkbuilding-results">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
            {language === 'nl' ? 'Linkbuilding resultaten' : 'Link building results'}
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#08080A] mb-4">
            {language === 'nl' ? 'Bewezen impact van linkbuilding' : 'Proven impact of link building'}
          </h2>
          <p className="text-[#555] text-lg leading-relaxed max-w-3xl mb-12">
            {language === 'nl'
              ? 'Linkbuilding is geen kwestie van zoveel mogelijk links verzamelen. Het gaat om de juiste links naar de juiste pagina\'s. Hieronder twee voorbeelden van pagina\'s waar gerichte linkbuilding heeft geleid tot significante groei in zowel verwijzende domeinen als organisch verkeer. Geverifieerd via zowel Ahrefs als Google Search Console.'
              : 'Link building is not about collecting as many links as possible. It is about getting the right links to the right pages. Below are two examples of pages where targeted link building led to significant growth in both referring domains and organic traffic. Verified through both Ahrefs and Google Search Console.'}
          </p>

          <div className="space-y-12">
            {/* Example 1 */}
            <div>
              <h3 className="text-xl font-bold text-[#08080A] mb-6">
                {language === 'nl' ? 'Voorbeeld 1' : 'Example 1'}
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="rounded-2xl border border-gray-100 overflow-hidden bg-white">
                  <div className="p-5 border-b border-gray-100">
                    <span className="bg-[#4286F4]/10 text-[#4286F4] text-xs font-bold px-2.5 py-1 rounded-full">Ahrefs</span>
                    <p className="text-[#555] text-sm mt-2">
                      {language === 'nl'
                        ? 'Verwijzende domeinen en organisch verkeer stijgen gelijktijdig na gerichte linkbuilding'
                        : 'Referring domains and organic traffic rising simultaneously after targeted link building'}
                    </p>
                  </div>
                  <img src="https://customer-assets.emergentagent.com/job_0357c4fc-e52a-47ac-a21c-eb4f9800644c/artifacts/e7acpwvf_Foto_op_canvas_Ahrefs.png" alt="Ahrefs linkbuilding result example 1" className="w-full" loading="lazy" />
                </div>
                <div className="rounded-2xl border border-gray-100 overflow-hidden bg-white">
                  <div className="p-5 border-b border-gray-100">
                    <span className="bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full">Google Search Console</span>
                    <p className="text-[#555] text-sm mt-2">
                      {language === 'nl'
                        ? '673 clicks (+104%) en 115K impressies (+135%) vs. dezelfde periode vorig jaar'
                        : '673 clicks (+104%) and 115K impressions (+135%) vs. same period last year'}
                    </p>
                  </div>
                  <img src="https://customer-assets.emergentagent.com/job_0357c4fc-e52a-47ac-a21c-eb4f9800644c/artifacts/z94iamzv_Foto_op_canvas_Google_search_console.png" alt="Google Search Console verification example 1" className="w-full" loading="lazy" />
                </div>
              </div>
            </div>

            {/* Example 2 */}
            <div>
              <h3 className="text-xl font-bold text-[#08080A] mb-6">
                {language === 'nl' ? 'Voorbeeld 2' : 'Example 2'}
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="rounded-2xl border border-gray-100 overflow-hidden bg-white">
                  <div className="p-5 border-b border-gray-100">
                    <span className="bg-[#4286F4]/10 text-[#4286F4] text-xs font-bold px-2.5 py-1 rounded-full">Ahrefs</span>
                    <p className="text-[#555] text-sm mt-2">
                      {language === 'nl'
                        ? 'Zelfde patroon: meer verwijzende domeinen resulteren in meer organisch verkeer'
                        : 'Same pattern: more referring domains result in more organic traffic'}
                    </p>
                  </div>
                  <img src="https://customer-assets.emergentagent.com/job_0357c4fc-e52a-47ac-a21c-eb4f9800644c/artifacts/tdkqrd8f_foto_op_plexiglas_Ahrefs.png" alt="Ahrefs linkbuilding result example 2" className="w-full" loading="lazy" />
                </div>
                <div className="rounded-2xl border border-gray-100 overflow-hidden bg-white">
                  <div className="p-5 border-b border-gray-100">
                    <span className="bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full">Google Search Console</span>
                    <p className="text-[#555] text-sm mt-2">
                      {language === 'nl'
                        ? '564 clicks (+134%) en 74.4K impressies (+140%) vs. dezelfde periode vorig jaar'
                        : '564 clicks (+134%) and 74.4K impressions (+140%) vs. same period last year'}
                    </p>
                  </div>
                  <img src="https://customer-assets.emergentagent.com/job_0357c4fc-e52a-47ac-a21c-eb4f9800644c/artifacts/9se5g57h_foto_op_plexiglas_Google_search_console.png" alt="Google Search Console verification example 2" className="w-full" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Education & Languages */}
      <section className="py-20 lg:py-28 bg-[#FAFAFA]" data-testid="education-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
                {language === 'nl' ? 'Opleiding' : 'Education'}
              </p>
              <h2 className="text-2xl font-bold text-[#08080A] mb-8">
                {language === 'nl' ? 'Academische achtergrond' : 'Academic background'}
              </h2>
              <div className="space-y-4">
                {[
                  { title: 'Bachelor of Business Administration', school: 'Fontys International Business School' },
                  { title: 'Minor in Digital Marketing', school: 'Avans University of Applied Sciences' },
                  { title: 'Exchange Student', school: 'Marshfield High School, USA' }
                ].map((edu, i) => (
                  <div key={i} className="p-5 rounded-2xl border border-gray-100 bg-white">
                    <h4 className="font-bold text-[#08080A] mb-1">{edu.title}</h4>
                    <p className="text-[#555] text-sm">{edu.school}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
                {language === 'nl' ? 'Talen' : 'Languages'}
              </p>
              <h2 className="text-2xl font-bold text-[#08080A] mb-8">
                {language === 'nl' ? 'Internationale ervaring' : 'International experience'}
              </h2>
              <div className="space-y-4">
                {[
                  { lang: language === 'nl' ? 'Nederlands' : 'Dutch', level: 'Native' },
                  { lang: language === 'nl' ? 'Engels' : 'English', level: 'Excellent' },
                  { lang: language === 'nl' ? 'Duits' : 'German', level: 'Basic' }
                ].map((item, i) => (
                  <div key={i} className="p-5 rounded-2xl border border-gray-100 bg-white flex items-center justify-between">
                    <span className="font-bold text-[#08080A]">{item.lang}</span>
                    <span className={`text-sm font-semibold ${item.level === 'Native' || item.level === 'Excellent' ? 'text-[#4286F4]' : 'text-[#999]'}`}>
                      {item.level}
                    </span>
                  </div>
                ))}
              </div>

              <a
                href="https://www.linkedin.com/in/falco-keiman/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 mt-8 text-[#555] hover:text-[#4286F4] transition-colors font-medium"
                data-testid="about-linkedin"
              >
                <div className="w-10 h-10 bg-white border border-gray-100 rounded-xl flex items-center justify-center">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </div>
                <span className="text-sm">LinkedIn profiel</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#4286F4] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <h2 className="text-2xl sm:text-3xl font-bold">
            {language === 'nl' ? 'Laten we samenwerken' : "Let's work together"}
          </h2>
          <div className="flex gap-4">
            <Link to={`${basePath}/contact`}>
              <button className="bg-white text-[#4286F4] font-semibold px-8 py-3.5 rounded-full hover:bg-gray-50 transition-colors inline-flex items-center gap-2">
                {language === 'nl' ? 'Neem contact op' : 'Get in touch'}
                <ArrowRight className="h-4 w-4" />
              </button>
            </Link>
            <a href="mailto:info@kesult.com">
              <button className="border-2 border-white/30 text-white font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors">
                {language === 'nl' ? 'Mail me' : 'Email me'}
              </button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
